import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { SparkleIcon, ArrowLeft } from '../components/ui/Icons';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const from = location.state?.from?.pathname || '/';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const user = await login(email, password);
            toast.success(`Vítejte zpět, ${user.name}!`);
            navigate(from, { replace: true });
        } catch (error) {
            const message = error.response?.data?.detail || 'Přihlášení se nezdařilo';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16" data-testid="login-page">
            {/* Sparkle */}
            <SparkleIcon className="w-6 h-10 text-foreground/60 mb-8" />
            
            {/* Header */}
            <h1 className="text-3xl md:text-4xl italic mb-2">
                Přihlášení
            </h1>
            <p className="text-muted-foreground italic mb-12">
                Vítejte zpět v Listovně
            </p>

            {/* Form */}
            <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
                <div className="form-field">
                    <label htmlFor="email" className="italic">E-mail</label>
                    <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="vas@email.cz"
                        required
                        className="bg-transparent border-0 border-b border-foreground/30 rounded-none focus:ring-0 focus:border-foreground px-0"
                        data-testid="login-email"
                    />
                </div>

                <div className="form-field">
                    <label htmlFor="password" className="italic">Heslo</label>
                    <div className="relative">
                        <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            required
                            className="bg-transparent border-0 border-b border-foreground/30 rounded-none focus:ring-0 focus:border-foreground px-0 pr-10"
                            data-testid="login-password"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-0 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-foreground"
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                <Button 
                    type="submit" 
                    variant="outline"
                    className="w-full italic mt-8"
                    disabled={loading}
                    data-testid="login-submit"
                >
                    {loading ? 'Přihlašování...' : 'Přihlásit se'}
                </Button>
            </form>

            {/* Register link */}
            <p className="text-center mt-8 text-muted-foreground">
                Nemáte účet?{' '}
                <Link 
                    to="/registrace" 
                    className="italic hover:underline underline-offset-4"
                    data-testid="register-link"
                >
                    Zaregistrujte se
                </Link>
            </p>

            {/* Back link */}
            <div className="mt-12 flex items-center gap-2">
                <ArrowLeft className="w-8 h-4 text-foreground/60" />
                <Link 
                    to="/" 
                    className="italic hover:underline underline-offset-4"
                >
                    zpět na homepage
                </Link>
            </div>
        </div>
    );
}
